import { useState, useMemo } from 'react';

import PropTypes from 'prop-types';
import { BsPlus, BsChevronDown, BsChevronRight, BsStars } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import Select from '#globalComponents/Forms/Select';
import Textarea from '#globalComponents/Forms/Textarea';
import TextInput from '#globalComponents/Forms/TextInput';
import Tag from '#globalComponents/Tag/Tag';

import { useCustomFieldDefs } from '../../../models/customFields/queries';
import { formatEuro, formatLossReason } from '../../../utils/formatters';
import DealExpandedRow from '../DealExpandedRow/DealExpandedRow';

const DealsSection = ({
	deals,
	contacts,
	company,
	members,
	stagesByPipeline,
	allProducts,
	allBundles,
	defaultStages,
	defaultPipeline,
	activeDealId,
	onActiveDealChange,
	onCreateDeal,
	onDealUpdate,
	onDealDelete,
	onStageTransition,
	onCreateContact,
}) => {
	const [creating, setCreating] = useState(false);
	const [draft, setDraft] = useState({
		title: '',
		description: '',
		dealValue: '',
		stageId: '',
		expectedCloseAt: '',
		userId: null,
	});
	const [submitting, setSubmitting] = useState(false);

	const { data: dealCustomFieldDefs = [] } = useCustomFieldDefs('deal');
	const dealCustomDefs = useMemo(
		() => dealCustomFieldDefs.filter((f) => !f.isDefault).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
		[dealCustomFieldDefs],
	);

	const resetDraft = () =>
		setDraft({
			title: '',
			description: '',
			dealValue: '',
			stageId: defaultStages[0]?.id ?? '',
			expectedCloseAt: '',
			userId: null,
		});

	const handleCreate = async () => {
		if (!draft.title.trim() || !defaultPipeline) return;
		setSubmitting(true);
		try {
			await onCreateDeal({
				title: draft.title.trim(),
				companyNameSnapshot: company.companyName,
				crmCompanyId: company.id,
				pipelineId: defaultPipeline.id,
				stageId: draft.stageId || defaultStages[0]?.id,
				dealValue: draft.dealValue ? Number(draft.dealValue) : null,
				expectedCloseAt: draft.expectedCloseAt || null,
				userId: draft.userId || null,
				description: draft.description.trim() || null,
				sourceType: 'manual',
			});
			setCreating(false);
			resetDraft();
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Card className="mb-4 overflow-hidden p-0">
			<div className="flex items-center justify-between px-4 py-3 border-b border-bgBase-500 bg-bgBase-300">
				<h3 className="text-xs-head text-text">
					Deals{' '}
					<span className="text-bgBase-800 font-normal normal-case tracking-normal">· {deals.length}</span>
				</h3>
				{!creating && (
					<Button
						onClick={() => {
							resetDraft();
							setCreating(true);
						}}
						variant="outline"
						size="sm"
					>
						<BsPlus className="w-3.5 h-3.5" /> Neuer Deal
					</Button>
				)}
			</div>

			{creating && (
				<div className="border-b-[1.5px] border-dashed border-primary-400 bg-gradient-to-b from-primary-100 to-white p-4 flex flex-col gap-3 animate-[fadeIn_0.15s_ease]">
					<div className="flex items-center gap-2 text-[13px] font-semibold text-text">
						<BsPlus className="w-3.5 h-3.5 text-primary-600" />
						Neuer Deal für {company.companyName}
					</div>
					<div className="grid grid-cols-2 gap-2.5">
						<div className="col-span-2 flex flex-col gap-1">
							<label className="text-xs-head text-bgBase-800">
								Titel <span className="text-warning-500">*</span>
							</label>
							<TextInput
								value={draft.title}
								onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
								placeholder="z. B. Enterprise-Plan 25 Seats"
								autoFocus
							/>
						</div>
						<div className="col-span-2 flex flex-col gap-1">
							<label className="text-xs-head text-bgBase-800">
								Beschreibung{' '}
								<span className="text-bgBase-800 font-normal normal-case tracking-normal ml-1">
									optional
								</span>
							</label>
							<Textarea
								value={draft.description}
								onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
								placeholder="Kurze Notiz zum Deal-Kontext …"
								rows={2}
							/>
						</div>
						<div className="flex flex-col gap-1">
							<label className="text-xs-head text-bgBase-800">Wert (€)</label>
							<TextInput
								type="number"
								min="0"
								step="0.01"
								value={draft.dealValue}
								onChange={(e) => setDraft((d) => ({ ...d, dealValue: e.target.value }))}
								placeholder="0"
							/>
						</div>
						<div className="flex flex-col gap-1">
							<label className="text-xs-head text-bgBase-800">Erwarteter Abschluss</label>
							<TextInput
								type="date"
								value={draft.expectedCloseAt}
								onChange={(e) => setDraft((d) => ({ ...d, expectedCloseAt: e.target.value }))}
								className="cursor-pointer"
							/>
						</div>
						<div className="flex flex-col gap-1">
							<label className="text-xs-head text-bgBase-800">Stage</label>
							<Select
								value={draft.stageId}
								onChange={(e) => setDraft((d) => ({ ...d, stageId: e.target.value }))}
								className="cursor-pointer"
							>
								{defaultStages.map((s) => (
									<option key={s.id} value={s.id}>
										{s.name}
									</option>
								))}
							</Select>
						</div>
						<div className="flex flex-col gap-1">
							<label className="text-xs-head text-bgBase-800">Owner</label>
							<Select
								value={draft.userId ?? ''}
								onChange={(e) => setDraft((d) => ({ ...d, userId: e.target.value || null }))}
								className="cursor-pointer"
							>
								<option value="">Nicht zugewiesen</option>
								{members.map((m) => (
									<option key={m.userId} value={m.userId}>
										{m.fullName ?? m.email}
									</option>
								))}
							</Select>
						</div>
					</div>
					<div className="flex items-center gap-2 pt-2 border-t border-bgBase-400">
						<span className="text-[10px] text-bgBase-800 inline-flex items-center gap-1 mr-auto">
							<BsStars className="w-3 h-3" /> Closing-Chance wird automatisch berechnet
						</span>
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={() => {
								setCreating(false);
								resetDraft();
							}}
						>
							Abbrechen
						</Button>
						<Button
							type="button"
							variant="solid"
							size="sm"
							onClick={handleCreate}
							disabled={!draft.title.trim() || submitting}
							loading={submitting}
						>
							Deal anlegen
						</Button>
					</div>
				</div>
			)}

			<div className="divide-y divide-bgBase-400">
				{deals.map((d) => {
					const active = d.id === activeDealId || deals.length === 1;
					const stages = stagesByPipeline[d.pipelineId] ?? [];
					const currentStage = stages.find((s) => s.id === d.stageId);
					const prob = d.closingChance !== null ? Math.round(Number(d.closingChance) * 100) : null;
					const canCollapse = deals.length > 1;
					const dealRowCols = canCollapse
						? '14px minmax(0, 1.6fr) minmax(0, 1fr) 110px 60px minmax(70px, 100px) minmax(60px, 90px)'
						: 'minmax(0, 1.6fr) minmax(0, 1fr) 110px 60px minmax(70px, 100px) minmax(60px, 90px)';

					return (
						<div
							key={d.id}
							className={`transition-colors ${active ? 'bg-gradient-to-b from-primary-100 to-transparent' : ''}`}
						>
							<button
								type="button"
								onClick={() => canCollapse && onActiveDealChange(active ? null : d.id)}
								className={`w-full text-left px-4 py-3 grid items-center gap-3 transition-colors ${canCollapse ? 'hover:bg-bgBase-300 cursor-pointer' : 'cursor-default'}`}
								style={{ gridTemplateColumns: dealRowCols }}
							>
								{canCollapse &&
									(active ? (
										<BsChevronDown className="w-3 h-3 text-bgBase-800" />
									) : (
										<BsChevronRight className="w-3 h-3 text-bgBase-800" />
									))}
								<div className="flex items-center gap-2 min-w-0">
									<span className="text-[13px] font-semibold text-text truncate">{d.title}</span>
									{d.status === 'won' && <Tag label="gewonnen" tone="success" size="xs" />}
									{d.status === 'lost' && (
										<span
											className="rounded-full bg-warning-200 px-1.5 py-0.5 text-[10px] text-warning-700"
											title={formatLossReason(d) ?? undefined}
										>
											verloren
										</span>
									)}
								</div>
								<div className="flex items-center gap-1.5 min-w-0 truncate">
									{currentStage ? (
										<span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-primary-200 text-primary-500">
											<span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-primary-500" />
											<span className="truncate">{currentStage.name}</span>
										</span>
									) : (
										<span className="text-xs text-bgBase-800">–</span>
									)}
								</div>
								<span className="text-[13px] font-semibold text-text tabular-nums text-right truncate">
									{formatEuro(d.dealValue)}
								</span>
								<span className="text-xs text-bgBase-800 tabular-nums text-right">
									{prob !== null ? `${prob}%` : '–'}
								</span>
								<span className="text-xs text-bgBase-800 tabular-nums whitespace-nowrap">
									{d.expectedCloseAt
										? new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: 'short' }).format(
												new Date(d.expectedCloseAt),
											)
										: '–'}
								</span>
								<div className="flex -space-x-1 flex-shrink-0 justify-end">
									{(d.users?.length > 0 ? d.users : []).slice(0, 3).map((u) => (
										<div
											key={u.id}
											className="w-6 h-6 rounded-full bg-primary-200 text-primary-700 flex items-center justify-center text-[9px] font-semibold border border-white"
										>
											{(u.fullName ?? '?')[0]?.toUpperCase()}
										</div>
									))}
									{(!d.users || d.users.length === 0) && (
										<div className="w-6 h-6 rounded-full bg-bgBase-400 text-bgBase-800 flex items-center justify-center text-[9px] font-semibold">
											?
										</div>
									)}
								</div>
							</button>

							{active && (
								<DealExpandedRow
									deal={d}
									contacts={contacts}
									stages={stages}
									members={members}
									allProducts={allProducts}
									allBundles={allBundles}
									dealCustomDefs={dealCustomDefs}
									onDealUpdate={onDealUpdate}
									onDealDelete={onDealDelete}
									onStageTransition={onStageTransition}
									onCreateContact={onCreateContact}
								/>
							)}
						</div>
					);
				})}
			</div>
		</Card>
	);
};

DealsSection.propTypes = {
	deals: PropTypes.array.isRequired,
	contacts: PropTypes.array.isRequired,
	company: PropTypes.object.isRequired,
	members: PropTypes.array.isRequired,
	stagesByPipeline: PropTypes.object.isRequired,
	allProducts: PropTypes.array.isRequired,
	allBundles: PropTypes.array.isRequired,
	defaultStages: PropTypes.array.isRequired,
	defaultPipeline: PropTypes.object,
	activeDealId: PropTypes.string,
	onActiveDealChange: PropTypes.func.isRequired,
	onCreateDeal: PropTypes.func.isRequired,
	onDealUpdate: PropTypes.func.isRequired,
	onDealDelete: PropTypes.func.isRequired,
	onStageTransition: PropTypes.func.isRequired,
	onCreateContact: PropTypes.func.isRequired,
};

export default DealsSection;
